import type { ReactElement } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmDeleteDialog } from '@/features/patient-record/components/_shared/ConfirmDeleteDialog';

import type { DeleteImpactDto, DeleteTargetType, MembersMode } from './api';
import { DeleteWizardDialog } from './DeleteWizardDialog';

/**
 * Whether a preview describes a cascade that reaches beyond the lone target — i.e. anything worth
 * walking the operator through the wizard for (extra pedigrees/individuals/families deleted, more than
 * one patient deleted, or any unlink). A truly isolated delete (just the target, no shared refs) takes
 * the plain confirm instead.
 */
function isMultiEntity(impact: DeleteImpactDto): boolean {
  return (
    impact.delete_pedigree_count > 0 ||
    impact.delete_individual_count > 0 ||
    impact.delete_family_count > 0 ||
    impact.delete_patient_count > 1 ||
    impact.unlink_node_count > 0 ||
    impact.unlink_family_guids.length > 0
  );
}

interface DeleteFlowDialogProps {
  readonly targetType: DeleteTargetType;
  readonly entityLabel: string;
  readonly previewStatus: 'pending' | 'error' | 'ready';
  readonly impact: DeleteImpactDto | undefined;
  readonly membersMode: MembersMode;
  readonly onMembersModeChange: (next: MembersMode) => void;
  readonly isPreviewFetching: boolean;
  readonly launching: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

/** Bare dialog shell for the non-interactive states (loading / error / blocked). */
function NoticeDialog({
  title,
  destructive,
  children,
  onCancel,
}: {
  readonly title: string;
  readonly destructive?: boolean;
  readonly children: ReactElement;
  readonly onCancel: () => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className={destructive === true ? 'flex items-center gap-2 text-destructive' : undefined}>
            {destructive === true && <AlertTriangle className="h-5 w-5" aria-hidden="true" />}
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>
        </DialogHeader>
        {children}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('ui.dialog.close', { defaultValue: 'Close' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Picks the right delete confirmation for a preview: a loading/error notice while the dry-run is in
 * flight or failed, a **blocked** notice (v1 fork stop-sign — surfaces `blocked_reason`, no confirm),
 * a **plain confirm** for an isolated single-entity delete, or the **wizard** (impact preview + f1/f2 +
 * type-to-confirm) for a family target or any multi-entity / confirmation-required cascade.
 */
export function DeleteFlowDialog({
  targetType,
  entityLabel,
  previewStatus,
  impact,
  membersMode,
  onMembersModeChange,
  isPreviewFetching,
  launching,
  onCancel,
  onConfirm,
}: DeleteFlowDialogProps): ReactElement {
  const { t } = useTranslation();

  if (previewStatus === 'pending' || impact === undefined) {
    return (
      <NoticeDialog title={t('delete.preview.loadingTitle', { defaultValue: 'Checking impact…' })} onCancel={onCancel}>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          {t('delete.preview.loading', { defaultValue: 'Working out what this delete affects…' })}
        </div>
      </NoticeDialog>
    );
  }

  if (previewStatus === 'error') {
    return (
      <NoticeDialog
        title={t('delete.preview.errorTitle', { defaultValue: 'Could not check impact' })}
        destructive
        onCancel={onCancel}
      >
        <p className="text-sm text-destructive">
          {t('delete.preview.error', {
            defaultValue: 'The impact of this delete could not be determined. Nothing was deleted.',
          })}
        </p>
      </NoticeDialog>
    );
  }

  if (impact.blocked) {
    return (
      <NoticeDialog
        title={t('delete.blocked.title', { defaultValue: 'Delete not available' })}
        destructive
        onCancel={onCancel}
      >
        <p className="text-sm text-foreground/90">
          {impact.blocked_reason ??
            t('delete.blocked.default', {
              defaultValue:
                'This record is shared as a structural node in another pedigree and cannot be deleted here yet. Unlink it from that pedigree first.',
            })}
        </p>
      </NoticeDialog>
    );
  }

  const simple = targetType !== 'FAMILY' && !impact.requires_confirmation && !isMultiEntity(impact);

  if (simple) {
    return (
      <ConfirmDeleteDialog
        title={t('delete.confirm.title', { defaultValue: 'Delete {{name}}?', name: entityLabel })}
        message={t('delete.confirm.message', {
          defaultValue: 'This permanently deletes {{name}}. Nothing else is affected. This cannot be undone.',
          name: entityLabel,
        })}
        confirmLabel={t('delete.confirm.action', { defaultValue: 'Delete' })}
        busy={launching}
        onCancel={onCancel}
        onConfirm={onConfirm}
      />
    );
  }

  return (
    <DeleteWizardDialog
      targetType={targetType}
      entityLabel={entityLabel}
      impact={impact}
      membersMode={membersMode}
      onMembersModeChange={onMembersModeChange}
      isPreviewFetching={isPreviewFetching}
      busy={launching}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}
